import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NavBar } from '../components/NavBar';
import { TaskCompleteModal } from '../components/TaskCompleteModal';
import { api } from '../lib/api';
import { TaskDetail } from './TaskDetail';
import type { Home, Task } from '@home-guard/shared';

// ---- Constants ----

const URGENCY_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#6b7280',
};

const URGENCY_LABEL: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const SECTION_STORAGE_KEY = 'housewizrd_task_sections';

type SectionKey = 'overdue' | 'dueSoon' | 'upcoming' | 'completed';

interface SectionState {
  overdue: boolean;
  dueSoon: boolean;
  upcoming: boolean;
  completed: boolean;
}

const DEFAULT_SECTIONS: SectionState = {
  overdue: true,
  dueSoon: true,
  upcoming: true,
  completed: false,
};

// ---- Form schema ----

const customTaskSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200).trim(),
  category: z.string().min(1, 'Category is required').max(100).trim(),
  urgency: z.enum(['critical', 'high', 'medium', 'low']),
  due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Select a date'),
  description: z.string().max(2000).trim().optional(),
});

type CustomTaskForm = z.infer<typeof customTaskSchema>;

// ---- Helpers ----

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function formatDueDate(dateStr: string): string {
  const due = new Date(dateStr + 'T00:00:00');
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - t.getTime()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays <= 14) return `In ${diffDays} days`;
  return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateLabel(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function loadSectionState(): SectionState {
  try {
    const raw = localStorage.getItem(SECTION_STORAGE_KEY);
    if (!raw) return DEFAULT_SECTIONS;
    return { ...DEFAULT_SECTIONS, ...JSON.parse(raw) as Partial<SectionState> };
  } catch {
    return DEFAULT_SECTIONS;
  }
}

function saveSectionState(s: SectionState): void {
  try {
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

function matchesSearch(task: Task, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    task.title.toLowerCase().includes(lower) ||
    task.category.toLowerCase().includes(lower) ||
    (task.description?.toLowerCase().includes(lower) ?? false)
  );
}

// ---- Page ----

export function TasksPage() {
  const [sections, setSections] = useState<SectionState>(loadSectionState);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [completeTask, setCompleteTask] = useState<Task | null>(null);
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });

  const homes = homesData?.data ?? [];
  const primaryHome = homes.find((h) => h.is_primary) ?? homes[0];

  const { data: pendingData, isLoading: loadingPending } = useQuery({
    queryKey: ['tasks', primaryHome?.id, 'pending'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${primaryHome!.id}/tasks?filter=pending`),
    enabled: !!primaryHome,
  });

  const { data: completeData, isLoading: loadingComplete } = useQuery({
    queryKey: ['tasks', primaryHome?.id, 'complete'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${primaryHome!.id}/tasks?filter=complete`),
    enabled: !!primaryHome,
  });

  const isLoading = loadingPending || loadingComplete;

  const { overdue, dueSoon, upcoming, completed } = useMemo(() => {
    const today = todayStr();
    const in14 = new Date();
    in14.setDate(in14.getDate() + 14);
    const in14Str = in14.toISOString().slice(0, 10);

    const pending = (pendingData?.data ?? []).filter((t) => matchesSearch(t, search));
    const done = (completeData?.data ?? []).filter((t) => matchesSearch(t, search));

    return {
      overdue: pending.filter((t) => t.due_date < today),
      dueSoon: pending.filter((t) => t.due_date >= today && t.due_date <= in14Str),
      upcoming: pending.filter((t) => t.due_date > in14Str),
      completed: done,
    };
  }, [pendingData, completeData, search]);

  function toggleSection(key: SectionKey) {
    setSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveSectionState(next);
      return next;
    });
  }

  function invalidateTasks() {
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['homes'] });
  }

  const [snoozingId, setSnoozingId] = useState<string | null>(null);

  async function handleSnooze(taskId: string) {
    setSnoozingId(taskId);
    try {
      await api.put(`/tasks/${taskId}/snooze`, { snooze_days: 7 });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } finally {
      setSnoozingId(null);
    }
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f3f4f6', paddingBottom: 88 }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '52px 16px 12px' }}>
        <h1 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: '#111827' }}>
          Maintenance Tasks
        </h1>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', fontSize: 16, pointerEvents: 'none',
          }}>🔍</span>
          <input
            type="search"
            placeholder="Search tasks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 36px',
              borderRadius: 10, border: '1px solid #e5e7eb',
              fontSize: 14, boxSizing: 'border-box', background: '#f9fafb',
              outline: 'none',
            }}
          />
        </div>
      </div>

      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <div style={{
              width: 28, height: 28, border: '3px solid #9333ea',
              borderTopColor: 'transparent', borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            <Section
              sectionKey="overdue"
              label="Overdue"
              count={overdue.length}
              accent="#ef4444"
              open={sections.overdue}
              onToggle={() => toggleSection('overdue')}
            >
              {overdue.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  showComplete
                  onComplete={() => setCompleteTask(task)}
                  onSnooze={() => handleSnooze(task.id)}
                  onView={() => setDetailTask(task)}
                  snoozing={snoozingId === task.id}
                />
              ))}
              {overdue.length === 0 && <EmptyState message="Nothing overdue — nice work!" />}
            </Section>

            <Section
              sectionKey="dueSoon"
              label="Due Soon"
              count={dueSoon.length}
              accent="#f59e0b"
              open={sections.dueSoon}
              onToggle={() => toggleSection('dueSoon')}
            >
              {dueSoon.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  showComplete
                  onComplete={() => setCompleteTask(task)}
                  onSnooze={() => handleSnooze(task.id)}
                  onView={() => setDetailTask(task)}
                  snoozing={snoozingId === task.id}
                />
              ))}
              {dueSoon.length === 0 && <EmptyState message="Nothing due in the next 14 days." />}
            </Section>

            <Section
              sectionKey="upcoming"
              label="Upcoming"
              count={upcoming.length}
              accent="#9333ea"
              open={sections.upcoming}
              onToggle={() => toggleSection('upcoming')}
            >
              {upcoming.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  showComplete
                  onComplete={() => setCompleteTask(task)}
                  onSnooze={() => handleSnooze(task.id)}
                  onView={() => setDetailTask(task)}
                  snoozing={snoozingId === task.id}
                />
              ))}
              {upcoming.length === 0 && <EmptyState message="No upcoming tasks." />}
            </Section>

            <Section
              sectionKey="completed"
              label="Completed"
              count={completed.length}
              accent="#16a34a"
              open={sections.completed}
              onToggle={() => toggleSection('completed')}
            >
              {completed.map((task) => (
                <CompletedTaskCard
                  key={task.id}
                  task={task}
                  onView={() => setDetailTask(task)}
                />
              ))}
              {completed.length === 0 && <EmptyState message="No completed tasks yet." />}
            </Section>
          </>
        )}
      </div>

      {/* FAB */}
      {primaryHome && (
        <button
          onClick={() => setShowAddModal(true)}
          aria-label="Add custom task"
          style={{
            position: 'fixed', bottom: 88, right: 20,
            width: 56, height: 56, borderRadius: '50%',
            background: '#9333ea', color: '#fff',
            border: 'none', fontSize: 28, lineHeight: 1,
            boxShadow: '0 4px 16px rgba(147,51,234,0.4)',
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 40,
          }}
        >
          +
        </button>
      )}

      {showAddModal && primaryHome && (
        <AddTaskModal
          homeId={primaryHome.id}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            invalidateTasks();
          }}
        />
      )}

      {completeTask && (
        <TaskCompleteModal
          taskId={completeTask.id}
          taskTitle={completeTask.title}
          onComplete={() => {
            setCompleteTask(null);
            invalidateTasks();
          }}
          onClose={() => setCompleteTask(null)}
        />
      )}

      {detailTask && (
        <TaskDetail
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onComplete={() => {
            setDetailTask(null);
            setCompleteTask(detailTask);
          }}
          onSnooze={async () => {
            await handleSnooze(detailTask.id);
            setDetailTask(null);
          }}
          completing={false}
          snoozing={snoozingId === detailTask.id}
        />
      )}

      <NavBar />
    </div>
  );
}

// ---- Section ----

function Section({
  label,
  count,
  accent,
  open,
  onToggle,
  children,
}: {
  sectionKey: SectionKey;
  label: string;
  count: number;
  accent: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0,
          }} />
          <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{label}</span>
          <span style={{
            fontSize: 12, fontWeight: 600, padding: '1px 8px', borderRadius: 12,
            background: accent + '18', color: accent,
          }}>
            {count}
          </span>
        </div>
        <span style={{ color: '#9ca3af', fontSize: 14, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          ▼
        </span>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid #f3f4f6', padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ---- Empty state ----

function EmptyState({ message }: { message: string }) {
  return (
    <p style={{ margin: '8px 4px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>{message}</p>
  );
}

// ---- Active task card ----

function TaskCard({
  task,
  showComplete,
  onComplete,
  onSnooze,
  onView,
  snoozing,
}: {
  task: Task;
  showComplete: boolean;
  onComplete: () => void;
  onSnooze: () => void;
  onView: () => void;
  snoozing: boolean;
}) {
  const today = todayStr();
  const isOverdue = task.due_date < today;
  const dot = URGENCY_COLOR[task.urgency] ?? '#9ca3af';
  const label = URGENCY_LABEL[task.urgency] ?? task.urgency;

  return (
    <div
      onClick={onView}
      style={{
        background: '#f9fafb', borderRadius: 12, padding: '12px 14px', cursor: 'pointer',
        border: isOverdue ? '1px solid #fee2e2' : '1px solid transparent',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0, marginTop: 6 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 14, lineHeight: 1.4 }}>
              {task.title}
            </p>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, flexShrink: 0,
              background: dot + '20', color: dot, textTransform: 'uppercase', letterSpacing: '0.03em',
            }}>
              {label}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{task.category}</span>
            <span style={{
              fontSize: 12, fontWeight: isOverdue ? 700 : 400,
              color: isOverdue ? '#ef4444' : '#9ca3af',
            }}>
              {formatDueDate(task.due_date)}
            </span>
            {task.estimated_cost_min != null && task.estimated_cost_max != null && (
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                ${task.estimated_cost_min}–${task.estimated_cost_max}
              </span>
            )}
            {task.recurrence_days && (
              <span style={{ fontSize: 11, background: '#ede9fe', color: '#7c3aed', padding: '1px 6px', borderRadius: 8 }}>
                Repeating
              </span>
            )}
            {task.is_custom && (
              <span style={{ fontSize: 11, background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 8 }}>
                Custom
              </span>
            )}
          </div>

          {task.description && (
            <p style={{ margin: '5px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
              {task.description.length > 120 ? task.description.slice(0, 120) + '…' : task.description}
            </p>
          )}

          {showComplete && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={onComplete}
                style={{
                  flex: 1, padding: '7px 10px',
                  background: '#16a34a', color: '#fff',
                  borderRadius: 8, border: 'none',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                ✓ Mark done
              </button>
              <button
                onClick={onSnooze}
                disabled={snoozing}
                style={{
                  padding: '7px 12px', background: '#fff',
                  color: snoozing ? '#d1d5db' : '#6b7280',
                  borderRadius: 8, border: '1px solid #e5e7eb',
                  fontSize: 13, fontWeight: 500, cursor: snoozing ? 'not-allowed' : 'pointer',
                }}
              >
                {snoozing ? '…' : 'Snooze 7d'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Completed task card (expandable) ----

function CompletedTaskCard({ task, onView }: { task: Task; onView: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const dot = URGENCY_COLOR[task.urgency] ?? '#9ca3af';

  return (
    <div style={{
      background: '#f9fafb', borderRadius: 12, overflow: 'hidden',
      border: '1px solid #e5e7eb',
    }}>
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{ padding: '12px 14px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center' }}
      >
        <span style={{ fontSize: 14, color: '#16a34a', flexShrink: 0 }}>✓</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0, fontWeight: 600, fontSize: 14, color: '#6b7280',
            textDecoration: 'line-through', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {task.title}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{task.category}</span>
            {task.completed_at && (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                Done {formatDateLabel(task.completed_at.slice(0, 10))}
              </span>
            )}
            <span style={{
              fontSize: 10, background: dot + '18', color: dot,
              padding: '1px 6px', borderRadius: 8, fontWeight: 600,
            }}>
              {URGENCY_LABEL[task.urgency] ?? task.urgency}
            </span>
          </div>
        </div>
        <span style={{ color: '#9ca3af', fontSize: 12, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          ▼
        </span>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 14px 12px', background: '#fff' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: task.notes ? 8 : 0 }}>
            {task.completed_at && (
              <div>
                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Completed</p>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151', fontWeight: 500 }}>
                  {formatDateLabel(task.completed_at.slice(0, 10))}
                </p>
              </div>
            )}
            {task.completed_cost != null && (
              <div>
                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cost</p>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151', fontWeight: 500 }}>
                  ${task.completed_cost.toFixed(2)}
                </p>
              </div>
            )}
          </div>
          {task.notes && (
            <div>
              <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{task.notes}</p>
            </div>
          )}
          <button
            onClick={onView}
            style={{
              marginTop: 10, padding: '6px 12px', background: 'none',
              border: '1px solid #e5e7eb', borderRadius: 8,
              fontSize: 12, color: '#6b7280', cursor: 'pointer', fontWeight: 500,
            }}
          >
            View full details
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Add task modal ----

function AddTaskModal({
  homeId,
  onClose,
  onSuccess,
}: {
  homeId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CustomTaskForm>({
    resolver: zodResolver(customTaskSchema),
    defaultValues: { urgency: 'medium', due_date: defaultDueDate() },
  });

  const onSubmit = async (data: CustomTaskForm) => {
    try {
      await api.post<Task>(`/homes/${homeId}/tasks`, {
        title: data.title,
        category: data.category,
        urgency: data.urgency,
        due_date: data.due_date,
        ...(data.description ? { description: data.description } : {}),
      });
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create task.';
      setError('root', { message: msg });
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px',
    borderRadius: 10, border: '1.5px solid #d1d5db',
    fontSize: 15, outline: 'none', boxSizing: 'border-box', background: '#fff',
    fontFamily: 'inherit',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 100, display: 'flex', alignItems: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', background: '#fff', borderRadius: '20px 20px 0 0',
          padding: '24px 20px 40px', maxHeight: '90dvh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Add custom task</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', padding: 4 }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
              Task title *
            </label>
            <input {...register('title')} placeholder="e.g. Replace HVAC filter" style={inputStyle} />
            {errors.title && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 3 }}>{errors.title.message}</p>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Category *</label>
              <input {...register('category')} placeholder="e.g. HVAC" style={inputStyle} />
              {errors.category && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 3 }}>{errors.category.message}</p>}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Urgency *</label>
              <select {...register('urgency')} style={inputStyle}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Due date *</label>
            <input {...register('due_date')} type="date" style={inputStyle} />
            {errors.due_date && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 3 }}>{errors.due_date.message}</p>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Notes (optional)</label>
            <textarea
              {...register('description')}
              rows={3}
              placeholder="Any additional details…"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {errors.root && (
            <div style={{ background: '#fef2f2', borderRadius: 10, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%', padding: '14px',
              background: isSubmitting ? '#c4b5fd' : '#9333ea',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Adding…' : 'Add task'}
          </button>
        </form>
      </div>
    </div>
  );
}
